import React, { ComponentType, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box as RawBox, BoxProps, Chip, Typography } from '@mui/material';
import {
  GroupedItems,
  GroupedSelectorDialogFC,
  Item,
} from '@provider-portal/common/components/TopBarProvider/GroupedSelectorDialogFC';
import { FontWeight } from '@provider-portal/common/styles/themeMui';

import { Select } from '@bolteu/kalep-react';

import { useAppIntl } from 'hooks/useAppIntl';
import { selectAssignedProviders, selectAssignedProvidersByVendor } from 'rdx/account/selectors';
import {
  ProviderPortalAssignedProvider,
  SelectedVendorAndProvider,
  VendorAssignedProvider,
} from 'rdx/account/types';
import { selectAccountVendorPermissions } from 'rdx/session/selectors';

require('@bolteu/kalep-react/build/kalep-versioned.css');

// material ui missed ref type for this component
const Box: ComponentType<BoxProps & { ref?: React.MutableRefObject<any> }> = RawBox;

const clearChipStyle = {
  backgroundColor: 'transparent',
  border: 'none',
  boxShadow: 'none',
};
const styles = {
  container: {
    overflow: 'hidden',
    '.MuiChip-root': {
      overflow: 'hidden',
    },
  },
  providerSelectorChip: {
    ...clearChipStyle,
    '&:hover,&:active': {
      ...clearChipStyle,
    },
  },
  providerSelector: {
    height: '40px',
  },
  providerVendorName: {
    fontWeight: FontWeight.SemiBold,
  },
  providerName: { marginLeft: '0.3em' },
};

export const PROVIDER_SELECT_ALL_LOCATIONS_OPTION_ID: number = 0;

export interface ProviderProps {
  selectedProvider: SelectedVendorAndProvider;
  onProviderSelect: (
    vendorId: number,
    vendorName: string,
    providerId: number,
    providerName: string,
  ) => void;
  displayAllLocationsOption: boolean;
  useKalepMainComponent: boolean;
}

export const Provider: React.FC<ProviderProps> = ({
  selectedProvider,
  onProviderSelect,
  displayAllLocationsOption,
  useKalepMainComponent,
}) => {
  const location = useLocation();
  const history = useHistory();
  const clearQueryParams = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    Array.from(searchParams.keys()).forEach((queryParamName) => {
      searchParams.delete(queryParamName);
      history.replace({ search: searchParams.toString() });
    });
  }, [location, history]);

  const intl = useAppIntl();
  const [isProviderSelectDialogOpen, setProviderSelectDialogOpen] = useState(false);

  const vendorPermissionsList = useSelector(selectAccountVendorPermissions);
  const vendorPermissionsSet = useMemo(() => {
    return new Set(vendorPermissionsList);
  }, [vendorPermissionsList]);

  const assignedProviders = useSelector(selectAssignedProviders);
  const assignedProvidersByVendor: Map<number, VendorAssignedProvider> | undefined = useSelector(
    selectAssignedProvidersByVendor,
  );

  const providerName: string = selectedProvider ? selectedProvider.providerName : '';
  const vendorName: string = selectedProvider ? selectedProvider.vendorName : '';

  const providersGrouped: GroupedItems[] = useMemo(() => {
    const data: GroupedItems[] = [];
    if (!assignedProvidersByVendor || assignedProvidersByVendor?.size === 0) {
      return [];
    }
    for (const [vendorId, vendorProviders] of assignedProvidersByVendor.entries()) {
      if (vendorProviders.providers.length !== 0) {
        const items: Item[] =
          displayAllLocationsOption && vendorPermissionsSet.has(vendorId)
            ? [
                {
                  id: `${PROVIDER_SELECT_ALL_LOCATIONS_OPTION_ID}`,
                  label: intl.formatMessage({
                    id: 'components.TopBar.providerSelectDialog.allLocationsOption',
                  }),
                },
              ]
            : [];
        vendorProviders.providers.forEach((provider: ProviderPortalAssignedProvider) => {
          items.push({ id: `${provider.provider_id}`, label: provider.provider_name });
        });

        data.push({
          groupId: `${vendorId}`,
          groupLabel: vendorProviders.vendor_name,
          items,
        });
      }
    }
    return data;
  }, [assignedProvidersByVendor, displayAllLocationsOption, intl, vendorPermissionsSet]);

  const providerSelectorStrings = useMemo(
    () => ({
      mainTitle: intl.formatMessage({ id: 'components.TopBar.providerSelectDialog.mainTitle' }),
      searchLabel: intl.formatMessage({ id: 'components.TopBar.providerSelectDialog.searchLabel' }),
      noSearchResultsTitle: intl.formatMessage({
        id: 'components.TopBar.providerSelectDialog.noSearchResultsTitle',
      }),
      noSearchResultsSubtitle: intl.formatMessage({
        id: 'components.TopBar.providerSelectDialog.noSearchResultsSubtitle',
      }),
    }),
    [intl],
  );

  const shouldHideProviderSelector = useMemo(() => {
    return (
      assignedProviders.length === 1 &&
      assignedProviders[0].provider_id === selectedProvider.providerId
    );
  }, [assignedProviders, selectedProvider]);

  // Handlers
  const handleOpen = useCallback(() => {
    setProviderSelectDialogOpen(true);
  }, []);

  const onClickCancelHandler = useCallback(() => {
    setProviderSelectDialogOpen(false);
  }, []);

  const handleProviderSelect = useCallback(
    (groupId: string, groupLabel: string, itemId: string, itemLabel: string) => {
      onProviderSelect(
        Number.parseInt(groupId, 10),
        groupLabel,
        Number.parseInt(itemId, 10),
        itemLabel,
      );

      setProviderSelectDialogOpen(false);
      clearQueryParams();
    },
    [onProviderSelect, clearQueryParams],
  );

  return (
    <Box mr={1} sx={styles.container}>
      <Box display="flex" alignItems="center">
        {useKalepMainComponent ? (
          <Select
            value={{
              title: `${vendorName}: ${providerName}`,
              value: `${vendorName}: ${providerName}`,
            }}
            options={[]}
            open={false}
            onClick={!shouldHideProviderSelector ? handleOpen : null}
            fullWidth
          />
        ) : (
          <Chip
            label={
              <>
                {vendorName && (
                  <Typography component="span" sx={styles.providerVendorName} variant="bodyL">
                    {vendorName}:
                  </Typography>
                )}
                <Typography component="span" sx={styles.providerName} variant="bodyL">
                  {providerName || intl.formatMessage({ id: 'components.TopBar.selectProvider' })}
                </Typography>
              </>
            }
            sx={styles.providerSelectorChip}
            variant="filled"
            onDelete={!shouldHideProviderSelector ? handleOpen : null}
            onClick={!shouldHideProviderSelector ? handleOpen : null}
            deleteIcon={<ExpandMoreIcon color="secondary" />}
          />
        )}
      </Box>
      {selectedProvider && !shouldHideProviderSelector && (
        <GroupedSelectorDialogFC
          open={isProviderSelectDialogOpen}
          strings={providerSelectorStrings}
          groupedItemsList={providersGrouped}
          selectedProviderId={selectedProvider.providerId}
          currentSelectedGroupId={`${selectedProvider.vendorId}`}
          currentSelectedGroupItemId={`${selectedProvider.providerId}`}
          onClickCancel={onClickCancelHandler}
          onSelectItem={handleProviderSelect}
        />
      )}
    </Box>
  );
};
